import { useState, useEffect, useCallback } from 'react';
import { updateSongVideo, VIDEO_KINDS } from '../../../../services/RepertoireService';
import { WARMUPS_SONG_ID } from '../../../../services/songNotesModel';
import { openVideoWindow, getYoutubeVideoId } from '../youtubeVideo';

/**
 * The song's saved videos — one per kind (lyrics / karaoke / performance).
 *
 * ── WHY THREE SLOTS AND NOT ONE ──
 * Anthony, 2026-08-31: "I'd like the option to be able to add all three, and
 * then have them saved to that profile the students have, so the next lesson
 * the teacher doesn't have to look it up again." The searching is the expensive
 * part of this feature — in attention, not in quota — and it should happen once
 * per song for the studio, not once per lesson per teacher.
 *
 * The slots live on the repertoire doc, so "the student's profile" is literal:
 * any teacher opening that song next week finds all three already there.
 *
 * NO SEARCH STATE HERE. This hook owns what is SAVED; useYoutubeSearch owns
 * finding a candidate. Keeping them apart is what lets the picker be opened
 * against any slot without this hook knowing the picker exists.
 */
const useSongVideo = ({ currentSong, setSongs, isPersistent }) => {
	// Which slot the picker is open against — null when it is closed. One piece
	// of state rather than a boolean plus a kind, so "open on karaoke" cannot
	// disagree with "open".
	const [editingKind, setEditingKind] = useState(null);
	const [draft, setDraft] = useState('');
	const [error, setError] = useState('');

	const videos = currentSong?.videos || {};
	const isWarmups = currentSong?.id === WARMUPS_SONG_ID;

	useEffect(() => {
		setEditingKind(null);
		setDraft('');
		setError('');
	}, [currentSong?.id]);

	/** Open a saved video in the shared popout window. */
	const open = useCallback(
		(kind) => {
			const url = videos[kind];
			if (!url) return;
			setError('');
			const title = [currentSong?.metadata?.title || currentSong?.name, kind].filter(Boolean).join(' — ');
			if (!openVideoWindow(url, title)) {
				setError('The video window was blocked. Allow pop-ups for this site, then try again.');
			}
		},
		[videos, currentSong?.metadata?.title, currentSong?.name]
	);

	const edit = useCallback(
		(kind) => {
			if (!VIDEO_KINDS.includes(kind)) return;
			setError('');
			setDraft(videos[kind] || '');
			setEditingKind(kind);
		},
		[videos]
	);

	const cancel = useCallback(() => {
		setEditingKind(null);
		setError('');
	}, []);

	/**
	 * Save into one slot. `urlOverride` beats the draft because picking a search
	 * result must not depend on setDraft having flushed — this callback closes
	 * over `draft`, so a setDraft/save pair in one handler saves the PREVIOUS
	 * value, i.e. the pick from one click ago.
	 */
	const save = useCallback(
		async (urlOverride, kindOverride) => {
			const kind = kindOverride || editingKind;
			if (!currentSong || isWarmups || !kind) return;

			const next = (typeof urlOverride === 'string' ? urlOverride : draft).trim();
			if (next && !/^https?:\/\//i.test(next) && !getYoutubeVideoId(next)) {
				setError('That does not look like a link. Paste the full URL, starting with https://');
				return;
			}
			setError('');
			setEditingKind(null);
			setSongs((prev) =>
				prev.map((s) =>
					s.id === currentSong.id ? { ...s, videos: { ...(s.videos || {}), [kind]: next || null } } : s
				)
			);
			if (isPersistent) {
				try {
					await updateSongVideo(currentSong.id, kind, next);
				} catch (err) {
					console.error('Error saving video to repertoire:', err);
					setError('The link did not save to the song. It still works this lesson — try again.');
				}
			}
		},
		[currentSong, isWarmups, editingKind, draft, isPersistent, setSongs]
	);

	const clear = useCallback((kind) => save('', kind), [save]);

	// Pass 3 A2: an error the teacher can put away (the next open clears it too).
	const clearError = useCallback(() => setError(''), []);

	return {
		videos,
		kinds: VIDEO_KINDS,
		savedCount: VIDEO_KINDS.filter((k) => videos[k]).length,
		editingKind,
		draft,
		setDraft,
		error,
		open,
		edit,
		cancel,
		save,
		clear,
		clearError,
	};
};

export default useSongVideo;
